// src/components/layout/PurchaseIntentModal.jsx
import { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/client";

const BLOCKERS = [
  { value: "price", label: "💸 Price" },
  { value: "need_time", label: "⏳ Need more time" },
  { value: "not_confident", label: "🤔 Not confident yet" },
  { value: "already_know", label: "✅ Already know this" },
  { value: "other", label: "💬 Other" },
];

export default function PurchaseIntentModal({ workbookId, onDone }) {
  const [score, setScore] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [blocker, setBlocker] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Set when the submit came back 401. Keeps the prompt open.
  const [authError, setAuthError] = useState(false);

  const handleSkip = useCallback(() => onDone(false), [onDone]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleSkip();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSkip]);

  const handleSubmit = async () => {
    if (!score) return;
    setSubmitting(true);
    setAuthError(false);
    try {
      await apiClient.post("/api/feedback/purchase-intent", {
        workbookId,
        likelihoodScore: score,
        blocker: blocker || null,
        freeText: text.trim() || null,
      });
      setSubmitted(true);
      setTimeout(() => onDone(true), 1400);
    } catch (err) {
      // A 401 is recoverable: hold the prompt open so the answer survives.
      if (err?.status === 401) setAuthError(true);
      else onDone(); // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  const likelyLabel = [
    "",
    "Very unlikely",
    "Unlikely",
    "Maybe",
    "Likely",
    "Definitely!",
  ];
  const likelyColor = [
    "",
    "#ef4444",
    "#f97316",
    "#f59e0b",
    "#22c55e",
    "#16a34a",
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleSkip}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,15,20,0.45)",
          backdropFilter: "blur(3px)",
          zIndex: 1000,
          animation: "piFadeIn 180ms ease",
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Upgrade feedback"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1001,
          width: "min(92vw, 460px)",
          background: "#F5F2EC",
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
          padding: "2rem",
          animation: "piSlideUp 220ms cubic-bezier(.22,.68,0,1.2)",
          fontFamily: "inherit",
        }}
      >
        {submitted ? (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <div style={{ fontSize: 36, marginBottom: ".5rem" }}>🙏</div>
            <p
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 18,
                fontWeight: 700,
                color: "#1a1a1a",
                margin: 0,
              }}
            >
              Thank you — this helps a lot!
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#f59e0b",
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    margin: "0 0 4px",
                  }}
                >
                  You've reached the end of the free content
                </p>
                <h3
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 19,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  Would you unlock the full bundle?
                </h3>
              </div>
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#a1a1aa",
                  fontSize: 20,
                  padding: "0 0 0 8px",
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Likelihood score */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                How likely are you to purchase?
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = score === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setScore(n)}
                      onMouseEnter={() => setHovered(n)}
                      onMouseLeave={() => setHovered(0)}
                      style={{
                        flex: 1,
                        border: active
                          ? `2px solid ${likelyColor[n]}`
                          : "2px solid #e4e4e7",
                        background: active ? `${likelyColor[n]}15` : "white",
                        color: active ? likelyColor[n] : "#71717a",
                        borderRadius: 8,
                        padding: "8px 0",
                        fontSize: 16,
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 120ms",
                        fontFamily: "inherit",
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              {(hovered || score) > 0 && (
                <p
                  style={{
                    fontSize: 12,
                    color: likelyColor[hovered || score],
                    margin: "6px 0 0",
                    fontWeight: 500,
                  }}
                >
                  {likelyLabel[hovered || score]}
                </p>
              )}
            </div>

            {/* Blocker pills */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                What's holding you back?{" "}
                <span style={{ fontWeight: 400, color: "#a1a1aa" }}>
                  (optional)
                </span>
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {BLOCKERS.map((b) => {
                  const active = blocker === b.value;
                  return (
                    <button
                      key={b.value}
                      onClick={() => setBlocker(active ? "" : b.value)}
                      style={{
                        border: active
                          ? "1.5px solid #2563eb"
                          : "1.5px solid #e4e4e7",
                        background: active ? "#eff6ff" : "white",
                        color: active ? "#2563eb" : "#52525b",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 120ms",
                        fontFamily: "inherit",
                      }}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional text */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Anything else?{" "}
                <span style={{ fontWeight: 400, color: "#a1a1aa" }}>
                  (optional)
                </span>
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. I'd upgrade if there were more real project examples..."
                maxLength={1000}
                rows={2}
                style={{
                  width: "100%",
                  border: "1.5px solid #e4e4e7",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  color: "#1a1a1a",
                  background: "white",
                  resize: "vertical",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                  lineHeight: 1.55,
                }}
              />
            </div>

            {/* A 401 keeps the prompt open with everything the user typed
                still in it. Closing on failure, which is what the silent
                onDone() below does for every other error, would throw the
                text away. */}
            {authError && (
              <div
                role="alert"
                style={{
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  color: "#78350F",
                  borderRadius: 8,
                  padding: "9px 12px",
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                Your session expired. Sign in again, then resend this.
              </div>
            )}

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  color: "#71717a",
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "8px 0",
                  fontFamily: "inherit",
                }}
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={!score || submitting}
                style={{
                  background: !score ? "#e4e4e7" : "#2563eb",
                  color: !score ? "#a1a1aa" : "white",
                  border: "none",
                  borderRadius: 9,
                  padding: "9px 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: !score ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background 150ms",
                }}
              >
                {submitting ? "Saving..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes piFadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes piSlideUp { from { opacity: 0; transform: translate(-50%, calc(-50% + 16px)) } to { opacity: 1; transform: translate(-50%, -50%) } }
      `}</style>
    </>
  );
}
